import { Fragment, Node } from '@tiptap/pm/model'
import { VFC, useEffect, useState } from 'react'
import { useTextEditor } from '../../TextEditor/context'
import { TextEditorInputProps } from '../types'

type TruncationHandlerProps = Pick<
  TextEditorInputProps,
  'truncate' | 'truncateOptions'
>

const getResolvedLimit = (rootNode: Node, limit: number) => {
  let emptyBlocks = 0
  const [from, to] = [0, Math.min(limit, rootNode.content.size)]
  rootNode.nodesBetween(from, to, (node, pos, parent, index) => {
    if (node.isBlock && !node.isText && index > 0) {
      emptyBlocks++
    }
  })

  return limit + emptyBlocks + 1
}

const TruncationHandler: VFC<TruncationHandlerProps> = ({
  truncate,
  truncateOptions,
}) => {
  const { editor } = useTextEditor()
  const [isTruncated, setTruncated] = useState(false)
  const [originalContent, setOriginalContent] = useState<Node | null>(null)
  const [originalEditableState, setOriginalEditableState] = useState(
    editor.isEditable
  )

  useEffect(() => {
    const { limit = null, ellipsis = false } = truncateOptions || {}

    if (truncate && limit !== null && !isTruncated) {
      const tr = editor.view.state.tr
      const resolvedLimit = getResolvedLimit(tr.doc, limit)
      // Only modify content if it exceeds the truncation limit
      if (tr.doc.content.size > resolvedLimit) {
        setOriginalContent(tr.doc)

        tr.replaceWith(
          resolvedLimit,
          tr.doc.content.size,
          ellipsis ? editor.schema.text('...') : Fragment.empty
        )
        // Tiptap doesn't fire the 'update' event for transactions
        // that have a `preventUpdate` meta key
        tr.setMeta('preventUpdate', true)
        editor.view.dispatch(tr)
      }

      // Always update truncate state & editable state
      setOriginalEditableState(editor.isEditable)
      editor.setEditable(false, false)
      setTruncated(true)
    } else if (!truncate && isTruncated) {
      if (originalContent !== null) {
        const tr = editor.view.state.tr
        tr.replaceWith(0, tr.doc.content.size, originalContent)
        tr.setMeta('preventUpdate', true)
        editor.view.dispatch(tr)

        setOriginalContent(null)
      }

      editor.setEditable(originalEditableState, false)
      setTruncated(false)
    }
  }, [
    editor,
    truncate,
    truncateOptions,
    isTruncated,
    originalContent,
    originalEditableState,
  ])

  return null
}

export default TruncationHandler
